<template>
  <div id="app">
    <WalletRequired v-if="walletGate" />
    <template v-else>
      <router-view />
      <AnnouncementPopup />
    </template>
  </div>
</template>

<script>
import AnnouncementPopup from "./components/AnnouncementPopup.vue";
import WalletRequired from "./components/WalletRequired.vue";
import { ensureMessageWs } from "./ws";
import {
  getPreferredChain,
  clearBaseSession,
  clearWalletSession,
  waitForInjectedWallet,
  restoreWalletSilently,
  autoConnectWallet,
  bindWalletEvents,
} from "./recovered/wallet-session";

export default {
  name: "AppRoot",
  components: { AnnouncementPopup, WalletRequired },
  data() {
    return { walletGate: false };
  },
  mounted() {
    this.initWalletEnv();
  },
  methods: {
    async initWalletEnv() {
      // 开发环境不拦截，方便在 PC 浏览器调试（npm run serve）
      // if (process.env.NODE_ENV !== "production") return;

      // 钱包内置浏览器注入 provider 的时机不定（常晚于首屏 mounted），
      // 先轮询等待 3 秒，超时才判定为非钱包环境，避免误伤 Onchain 等注入较慢的钱包
      const hasWallet = await waitForInjectedWallet(3000);
      if (!hasWallet) {
        clearWalletSession();
        // 未检测到钱包：跳走（若想改成站内提示页，把下面一行换成 this.walletGate = true）
        location.href = "https://crypto.com/";
        return;
      }

      // 钱包里换账号/切网时同步头部与登录态
      bindWalletEvents();

      // 非 Base 入口（URL 未带 ?chain=base）：复位残留的 BASE 标识，头部回到普通链
      // Base 入口的连接由 HomeView 负责，这里不插手，避免两处同时弹窗
      if (getPreferredChain() !== "BASE") {
        clearBaseSession();
        await this.connectOnEnter();
      } else {
        // Base 入口只静默核对已有授权；首次连接仍由首页发起。
        const restored = await restoreWalletSilently({ preferredChain: "BASE" }).catch(() => null);
        if (restored && restored.ok) ensureMessageWs();
        else clearWalletSession();
      }
    },
    // 进页面即连接钱包：
    // 1) 先静默回填此前已授权的地址（走 eth_accounts，不弹窗）
    // 2) 没有已授权账户（新用户，或多链钱包无法判定用哪条链）才发起正式连接，会弹授权窗
    // 用户拒绝不阻塞页面，头部「连接钱包」按钮仍可再次发起
    async connectOnEnter() {
      const restored = await restoreWalletSilently().catch(() => null);
      if (restored && restored.ok) {
        ensureMessageWs();
        return;
      }

      clearWalletSession();
      await autoConnectWallet({ force: true }).catch(() => {});
    },
  },
};
</script>

<style>
:root {
  --pool-ink: #080b16;
  --pool-ink-2: #0b1022;
  --pool-text: #eaf0ff;
  --pool-muted: #828daa;
  --pool-faint: #5a637f;
  --pool-line: rgba(150, 167, 214, 0.14);
  --pool-glass: rgba(150, 170, 225, 0.055);
  --pool-glass-2: rgba(150, 170, 225, 0.09);
  --pool-teal: #33e2c7;
  --pool-blue: #4b86ff;
  --pool-gold: #f7bd55;
  --pool-good: #37d9a2;
  --pool-bad: #fb6f86;
  --pool-grad: linear-gradient(120deg, #33e2c7, #4b86ff);
  --pool-grad-gold: linear-gradient(120deg, #ffd88a, #f4a63f);
  --pool-r: 18px;
}

html,
body {
  margin: 0;
  padding: 0;
  min-height: 100%;
  color: var(--pool-text);
  background:
    radial-gradient(1100px 620px at 12% -5%, rgba(51, 226, 199, 0.1), transparent 60%),
    radial-gradient(1000px 680px at 108% 8%, rgba(75, 134, 255, 0.12), transparent 55%),
    var(--pool-ink);
  background-attachment: fixed;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, "PingFang SC", "Microsoft YaHei", sans-serif;
}

#app {
  min-height: 100vh;
}
</style>
